import { TaskAdmin } from '@gleamer/types';
import React, { useState } from 'react';
import {
  useComputationIssues,
  useStopComputation,
  useTaskComputations,
} from '../../../../hooks/useTaskActivity';
import { PageButton } from '../../../atoms/PageButton';
import { formatDateTime } from '../imports/format';
import { TraceLink } from '../TraceLink';
import {
  ComputationStatusBadge,
  Counts,
  Pager,
  SeverityBadge,
} from './ActivityParts';
import { formatDuration, progressPercent, TRIGGER_LABELS } from './activity';

type TaskComputation = TaskAdmin.TaskComputation;
type TaskComputationSummary = TaskAdmin.TaskComputationSummary;

/** Issues read per page. */
export const ISSUES_PAGE_SIZE = 50;

/** A computation's issues, a page at a time, with their total. */
function ComputationIssues({ computation }: { computation: TaskComputation }) {
  const [offset, setOffset] = useState(0);
  const live = computation.status === 'RUNNING';
  const issues = useComputationIssues(
    computation.id,
    { limit: ISSUES_PAGE_SIZE, offset },
    live
  );
  if (issues.isLoading) {
    return <p className="text-sm text-dl-muted">Loading the issues…</p>;
  }
  if (issues.isError) {
    return (
      <p role="alert" className="text-sm text-dl-danger">
        Could not load the issues: {(issues.error as Error)?.message}
      </p>
    );
  }
  const data = issues.data;
  if (!data || data.total === 0) {
    return <p className="text-sm text-dl-muted">No issue recorded.</p>;
  }
  return (
    <div>
      <div className="overflow-x-auto">
        <table
          className="w-full table-auto text-sm"
          aria-label={`Issues of computation ${computation.id}`}
        >
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-dl-faint">
              <th className="py-1 pr-2">Subject</th>
              <th className="py-1 pr-2">Severity</th>
              <th className="py-1 pr-2">Reason</th>
              <th className="py-1">Detail</th>
            </tr>
          </thead>
          <tbody>
            {data.issues.map((issue, index) => (
              <tr
                key={`${issue.subject}-${issue.reason}-${index}`}
                className="border-t border-dl-border align-top"
              >
                <td className="break-all py-1 pr-2 font-mono">
                  {issue.subject}
                </td>
                <td className="py-1 pr-2">
                  <SeverityBadge severity={issue.severity} />
                </td>
                <td className="py-1 pr-2 font-mono">{issue.reason}</td>
                <td className="break-words py-1 text-dl-muted">
                  {issue.detail ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager
        label="issues"
        total={data.total}
        limit={ISSUES_PAGE_SIZE}
        offset={offset}
        onOffset={setOffset}
      />
    </div>
  );
}

function settledOf(
  computation: TaskComputation,
  summary?: TaskComputationSummary | null
): number {
  if (computation.settledItems !== undefined) {
    return computation.settledItems;
  }
  if (summary && summary.id === computation.id) {
    return summary.settledItems;
  }
  return computation.computedItems + computation.failedItems;
}

/**
 * Stops the running computation, after asking inline: for one that will not finish (stalled), so the task is released
 * to its labellers. Reload the task to compute its items again.
 */
function StopComputation({ taskId, computation }: { taskId: number; computation: TaskComputation }) {
  const stop = useStopComputation(taskId);
  const [asking, setAsking] = useState(false);
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {asking ? (
        <>
          <span className="text-sm">
            Stop this computation? The task is released to its labellers; reload it to compute its items again.
          </span>
          <PageButton
            tone="stop"
            onClick={() => {
              setAsking(false);
              stop.mutate(computation.id);
            }}
          >
            Confirm stop
          </PageButton>
          <PageButton onClick={() => setAsking(false)}>Cancel</PageButton>
        </>
      ) : (
        <PageButton tone="stop" onClick={() => setAsking(true)} disabled={stop.isLoading}>
          {stop.isLoading ? 'Stop…' : 'Stop'}
        </PageButton>
      )}
      {stop.isError && (
        <span role="alert" className="text-sm text-dl-danger">
          {(stop.error as Error).message}
        </span>
      )}
    </span>
  );
}

/** The latest computation: where it stands, its counts, its failure and its issues. */
function LatestComputation({
  taskId,
  computation,
  summary,
  canStop,
}: {
  taskId: number;
  computation: TaskComputation;
  summary?: TaskComputationSummary | null;
  canStop: boolean;
}) {
  const running = computation.status === 'RUNNING';
  const settled = settledOf(computation, summary);
  const percent = progressPercent(settled, computation.expectedItems);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <ComputationStatusBadge status={computation.status} />
        <span className="text-dl-muted">
          {TRIGGER_LABELS[computation.trigger]} · revision{' '}
          {computation.labelTaskRevision} · started{' '}
          {formatDateTime(computation.startedAt)} ·{' '}
          {running ? 'running for ' : 'took '}
          {formatDuration(computation.startedAt, computation.finishedAt)}
        </span>
        <TraceLink traceId={computation.traceId} logsUrl={computation.logsUrl} />
      </div>

      {running && (
        <div>
          <div
            role="progressbar"
            aria-label="Items settled"
            aria-valuemin={0}
            aria-valuemax={computation.expectedItems ?? undefined}
            aria-valuenow={settled}
            className="h-3 w-full overflow-hidden rounded bg-dl-track"
          >
            {percent !== null && (
              <div
                className="h-full bg-dl-primary"
                style={{ width: `${percent}%` }}
              />
            )}
          </div>
          <p className="mt-1 text-sm text-dl-muted">
            {computation.expectedItems === null
              ? 'Reading the dataset table…'
              : `${settled.toLocaleString()} of ${computation.expectedItems.toLocaleString()} items (${percent} %)`}
          </p>
        </div>
      )}

      {running && computation.stalled && (
        <p
          role="alert"
          className="rounded-lg bg-dl-warning-tint px-3 py-2 text-sm font-medium text-dl-warning"
        >
          No progress since {formatDateTime(computation.updatedAt)}: the
          computation may be stuck.
        </p>
      )}
      {running && canStop && (
        <div>
          <StopComputation taskId={taskId} computation={computation} />
        </div>
      )}

      <Counts
        counts={[
          { label: 'Rows read', value: computation.sourceRows },
          { label: 'Passes read', value: computation.initialPasses },
          { label: 'Expected', value: computation.expectedItems },
          { label: 'Computed', value: computation.computedItems },
          { label: 'Removed', value: computation.removedItems },
          { label: 'Relabelled', value: computation.relabelledItems },
          {
            label: 'Failed',
            value: computation.failedItems,
            tone: 'text-dl-danger',
          },
        ]}
      />

      {computation.failure && (
        <p
          role="alert"
          className="whitespace-pre-wrap break-words rounded-lg bg-dl-danger-tint px-3 py-2 text-sm text-dl-danger"
        >
          {computation.failure}
        </p>
      )}

      <div>
        <h5 className="mb-1 font-semibold text-dl-text">Issues</h5>
        <ComputationIssues computation={computation} />
      </div>
    </div>
  );
}

/** One earlier computation on a line, unfolding to its issues. */
function EarlierComputation({ computation }: { computation: TaskComputation }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="border-t border-dl-border py-1.5">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className="flex w-full flex-wrap items-center gap-2 text-left text-sm"
      >
        <span aria-hidden>{open ? '▾' : '▸'}</span>
        <ComputationStatusBadge status={computation.status} />
        <span className="text-dl-text">
          {TRIGGER_LABELS[computation.trigger]} · rev.{' '}
          {computation.labelTaskRevision}
        </span>
        <span className="text-dl-muted">
          {formatDateTime(computation.startedAt)} ·{' '}
          {computation.computedItems.toLocaleString()} computed
          {computation.issues > 0 &&
            ` · ${computation.issues.toLocaleString()} issues`}
        </span>
      </button>
      {open && (
        <div className="mt-2 flex flex-col gap-2 pl-5">
          <TraceLink traceId={computation.traceId} logsUrl={computation.logsUrl} />
          {computation.failure && (
            <p className="whitespace-pre-wrap break-words text-sm text-dl-danger">
              {computation.failure}
            </p>
          )}
          <ComputationIssues computation={computation} />
        </div>
      )}
    </li>
  );
}

/**
 * The task's items: the latest computation of them — read again every 5 s while it runs — and the ones before.
 * `summary` is the task's own summary of its latest computation, which carries its progress.
 */
export function ItemsSection({
  taskId,
  summary,
  canStop = false,
}: {
  taskId: number;
  summary?: TaskComputationSummary | null;
  /** update:labelTask: a running computation can be stopped. */
  canStop?: boolean;
}) {
  const computations = useTaskComputations(taskId);
  const [latest, ...earlier] = computations.data ?? [];
  return (
    <section aria-label="Items">
      <h4 className="mb-2 text-lg font-semibold text-dl-text">Items</h4>
      {computations.isLoading && (
        <p className="text-sm text-dl-muted">Loading the computations…</p>
      )}
      {computations.isError &&
        (/^404\b/.test((computations.error as Error)?.message ?? '') ? (
          <p className="text-sm text-dl-muted">
            This viewer-api does not record the computations of a task's items
            yet.
          </p>
        ) : (
          <p role="alert" className="text-sm text-dl-danger">
            Could not load the computations of its items:{' '}
            {(computations.error as Error)?.message}
          </p>
        ))}
      {computations.isSuccess && !latest && (
        <p className="text-sm text-dl-muted">
          No computation of its items on record.
        </p>
      )}
      {latest && <LatestComputation taskId={taskId} canStop={canStop} computation={latest} summary={summary} />}
      {earlier.length > 0 && (
        <div className="mt-4">
          <h5 className="mb-1 font-semibold text-dl-text">
            Earlier computations
          </h5>
          <ul>
            {earlier.map(c => (
              <EarlierComputation key={c.id} computation={c} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
